import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import ConfirmDialog from '../components/ConfirmDialog';
import FacePhotoLightbox from '../components/FacePhotoLightbox';
import '../css/parvekshak.css';
import '../css/knownfaces.css';

function avatarColor(name) {
  const palette = [
    '#2ecc71','#3498db','#9b59b6','#e67e22',
    '#1abc9c','#e91e63','#00bcd4','#ff5722',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

// Single photo thumbnail
function PhotoThumb({ name, file, profile, onDelete, onClick }) {
  const [src,        setSrc]        = useState(null);
  const [errored,    setErrored]    = useState(false);
  const [delBusy,    setDelBusy]    = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    setErrored(false);
    apiImageUrl(`/ui/faces/known/${encodeURIComponent(name)}/photo/${encodeURIComponent(file)}`)
      .then(url => setSrc(url));
  }, [name, file]);

  const doDelete = async () => {
    setConfirmDel(false);
    setDelBusy(true);
    try {
      await apiFetch(`/ui/faces/known/${encodeURIComponent(name)}/photo/${encodeURIComponent(file)}`, { method: 'DELETE' });
      onDelete(file);
    } catch {
      setDelBusy(false);
    }
  };

  return (
    <>
    <div className="kf-thumb" onClick={() => src && !errored && onClick?.(src)}>
      {src && !errored
        ? <img src={src} alt={file} className="kf-thumb-img" style={{ cursor: 'zoom-in' }}
            onError={() => setErrored(true)} />
        : <div className="kf-thumb-placeholder">{src ? '⚠️' : '⏳'}</div>
      }
      <span className={`kf-thumb-badge ${profile === 'night' ? 'night' : profile === 'auto' ? 'auto' : 'day'}`}>
        {profile === 'night' ? '🌙' : profile === 'auto' ? '🤖' : '☀️'}
      </span>
      <button className="kf-thumb-del" onClick={e => { e.stopPropagation(); setConfirmDel(true); }} disabled={delBusy} title="Delete photo">
        {delBusy ? '…' : '✕'}
      </button>
    </div>
    {confirmDel && (
      <ConfirmDialog title="Delete this photo?" confirmLabel="Delete"
        onConfirm={doDelete} onCancel={() => setConfirmDel(false)} />
    )}
    </>
  );
}

// Per-person expanded card
function PersonCard({ person, showToast, expanded, onToggle, onRemoved, onBanned, onRenamed }) {
  const [photos,        setPhotos]        = useState(null);
  const [lightbox,      setLightbox]      = useState(null);
  const [removeBusy,    setRemoveBusy]    = useState(false);
  const [banBusy,       setBanBusy]       = useState(false);
  const [renaming,      setRenaming]      = useState(false);
  const [newName,       setNewName]       = useState('');
  const [renameBusy,    setRenameBusy]    = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmBan,    setConfirmBan]    = useState(false);
  const renameInputRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const r = await apiFetch(`/ui/faces/known/${encodeURIComponent(person.name)}/photos`);
      const d = await r.json();
      setPhotos(d.photos || []);
    } catch {
      setPhotos([]);
    }
  }, [person.name]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const doRemove = async () => {
    setConfirmRemove(false);
    setRemoveBusy(true);
    try {
      await apiFetch(`/faces/known/${encodeURIComponent(person.name)}`, { method: 'DELETE' });
      onRemoved(person.name);
      showToast(`${person.name} removed`);
    } catch {
      setRemoveBusy(false);
      showToast('Failed to remove', 'err');
    }
  };

  const doBan = async () => {
    setConfirmBan(false);
    setBanBusy(true);
    try {
      await apiFetch(`/faces/known/${encodeURIComponent(person.name)}/move_to_banned`, { method: 'POST' });
      onBanned(person.name);
      showToast(`${person.name} banned`);
    } catch {
      setBanBusy(false);
      showToast('Failed to ban', 'err');
    }
  };

  const startRename = (e) => {
    e.stopPropagation();
    setNewName(person.name);
    setRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };

  const cancelRename = (e) => {
    e?.stopPropagation();
    setRenaming(false);
    setNewName('');
  };

  const submitRename = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const n = newName.trim();
    if (!n || n === person.name) { cancelRename(); return; }
    setRenameBusy(true);
    try {
      await apiFetch(`/faces/known/${encodeURIComponent(person.name)}/rename?new_name=${encodeURIComponent(n)}`, { method: 'PUT' });
      setRenaming(false);
      onRenamed(person.name, n);
      showToast(`Renamed to ${n}`);
    } catch (err) {
      const msg = await err?.response?.json?.().catch?.(() => null);
      showToast(msg?.detail || 'Rename failed', 'err');
      setRenameBusy(false);
    }
  };

  const color = avatarColor(person.name);

  return (
    <>
      <div className="kf-card">
        <div className="kf-card-top" onClick={onToggle} style={{ cursor: 'pointer' }}>
          <div className="kf-avatar" style={{ background: color }}>
            {person.name.charAt(0).toUpperCase()}
          </div>
          <div className="kf-card-info">
            {renaming ? (
              <form onSubmit={submitRename} onClick={e => e.stopPropagation()}
                style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  ref={renameInputRef}
                  className="alert-inline-input"
                  style={{ padding: '5px 10px', fontSize: 14, width: 160, margin: 0 }}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  disabled={renameBusy}
                  onKeyDown={e => e.key === 'Escape' && cancelRename()}
                />
                <button type="submit" className="pk-btn primary" disabled={renameBusy}
                  style={{ padding: '5px 12px', fontSize: 12, height: 30 }}>
                  {renameBusy ? '…' : '✓ Save'}
                </button>
                <button type="button" className="pk-btn" disabled={renameBusy}
                  onClick={cancelRename} style={{ padding: '5px 10px', fontSize: 12, height: 30 }}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <p className="kf-card-name">{person.name}</p>
                <div className="kf-card-meta">
                  {person.has_day   && <span className="badge badge-day">☀️ Day</span>}
                  {person.has_night && <span className="badge badge-night">🌙 Night</span>}
                  {person.has_auto  && <span className="badge badge-auto">🤖 Auto</span>}
                  <span className="badge badge-cam">
                    {photos !== null ? photos.length : person.file_count} photo{(photos?.length ?? person.file_count) !== 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
          {!renaming && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
              <button className="kf-icon-btn" title="Rename"
                onClick={startRename} disabled={removeBusy || banBusy}
                style={{ fontSize: 13 }}>
                ✏️
              </button>
              <button className="kf-icon-btn remove" onClick={e => { e.stopPropagation(); setConfirmRemove(true); }} disabled={removeBusy}>
                {removeBusy ? '…' : '✕'}
              </button>
              <button className="kf-icon-btn ban" onClick={e => { e.stopPropagation(); setConfirmBan(true); }} disabled={banBusy}>
                {banBusy ? '…' : '🚫'}
              </button>
            </div>
          )}
          <span className="kf-chevron" style={{ marginLeft: 8 }}>{expanded ? '▲' : '▼'}</span>
        </div>

        {expanded && (
          <div className="kf-expanded">
            {photos === null ? (
              <div className="kf-photos-loading">⏳ Loading photos…</div>
            ) : photos.length === 0 ? (
              <div className="kf-photos-empty">No photos stored</div>
            ) : (
              <div className="kf-photos-strip">
                {photos.map((p, i) => (
                  <PhotoThumb
                    key={p.file}
                    name={person.name}
                    file={p.file}
                    profile={p.profile}
                    onDelete={file => setPhotos(prev => prev.filter(x => x.file !== file))}
                    onClick={src => setLightbox({ idx: i, src })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {lightbox && photos && (
        <FacePhotoLightbox
          photos={photos}
          idx={lightbox.idx}
          initialSrc={lightbox.src}
          personName={person.name}
          kind="known"
          onClose={() => setLightbox(null)}
        />
      )}
      {confirmRemove && (
        <ConfirmDialog title={`Remove ${person.name}?`} message="They will be removed from known faces."
          confirmLabel="Remove" onConfirm={doRemove} onCancel={() => setConfirmRemove(false)} />
      )}
      {confirmBan && (
        <ConfirmDialog title={`Ban ${person.name}?`} message="They will be moved to the banned list."
          confirmLabel="Ban" confirmClass="danger" onConfirm={doBan} onCancel={() => setConfirmBan(false)} />
      )}
    </>
  );
}

// ─── AddPersonModal — with existing-contact picker (mirrors Telegram flow) ────
function AddPersonModal({ onClose, onAdded, existingPersons }) {
  // step: 'pick' | 'new'
  const [step,    setStep]    = useState('pick');
  const [name,    setName]    = useState('');
  const [profile, setProfile] = useState('day');
  const [file,    setFile]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');
  const fileRef  = useRef();
  const inputRef = useRef();

  // Auto-focus name input when switching to new step
  useEffect(() => {
    if (step === 'new') inputRef.current?.focus();
  }, [step]);

  const uploadPhoto = async (targetName) => {
    if (!file) { setErr('Select a photo'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiFetch(`/faces/known/${encodeURIComponent(targetName)}/photos?profile=${profile}`, {
        method: 'POST', body: fd,
      });
      onAdded(targetName);
    } catch {
      setErr('Upload failed — check server connection');
      setBusy(false);
    }
  };

  const handleExistingPick = async (existingName) => {
    if (!file) {
      // No file yet — switch to new step pre-filled with name so user can pick a photo
      setName(existingName);
      setStep('new');
      return;
    }
    await uploadPhoto(existingName);
  };

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n)    { setErr('Enter a name'); return; }
    if (!file) { setErr('Select a photo'); return; }
    await uploadPhoto(n);
  };

  return (
    <div className="pk-lightbox" onClick={onClose}
      style={{ padding: '24px 16px', overflowY: 'auto', alignItems: 'center', justifyContent: 'flex-start' }}>
      <div
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          padding: 28, width: '100%', maxWidth: 400,
          flexShrink: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text)' }}>
            {step === 'new' ? '👤 New Person' : '👤 Add Known Person'}
          </h3>
          <button className="pk-lightbox-close" style={{ position: 'static' }} onClick={onClose}>✕</button>
        </div>

        {/* ── PICK STEP ── */}
        {step === 'pick' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {existingPersons.length > 0 && (
              <>
                <p style={{ margin: '0 0 2px', fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                  Add photo to existing person
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                  {existingPersons.map(pName => (
                    <button
                      key={pName}
                      onClick={() => handleExistingPick(pName)}
                      disabled={busy}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: 'var(--surface2, rgba(255,255,255,0.04))',
                        border: '1px solid var(--border)', borderRadius: 8,
                        padding: '9px 14px', cursor: 'pointer', color: 'var(--text)',
                        fontSize: 14, textAlign: 'left', transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover, rgba(255,255,255,0.08))'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface2, rgba(255,255,255,0.04))'}
                    >
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: avatarColor(pName),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#fff',
                      }}>
                        {pName.charAt(0).toUpperCase()}
                      </span>
                      {pName}
                    </button>
                  ))}
                </div>

                {/* Profile + photo picker shown in pick step so user can pre-select before picking a person */}
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Profile</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['day', 'night'].map(p => (
                        <button key={p} type="button"
                          className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                          onClick={() => setProfile(p)}>
                          {p === 'day' ? '☀️ Day' : '🌙 Night'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Photo</label>
                    <div className="pk-upload-zone" onClick={() => fileRef.current.click()}
                      style={{ padding: '8px', fontSize: 12 }}>
                      {file ? `✅ ${file.name}` : '📷 Select photo…'}
                      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => setFile(e.target.files[0] || null)} />
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              </>
            )}

            {err && (
              <div style={{ fontSize: 12, color: '#e74c3c', padding: '6px 10px',
                background: 'rgba(231,76,60,0.08)', borderRadius: 6, border: '1px solid rgba(231,76,60,0.2)' }}>
                {err}
              </div>
            )}
            {busy && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>⏳ Uploading…</div>}

            <button
              onClick={() => { setErr(''); setStep('new'); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: 'transparent',
                border: '1px dashed #2ecc71',
                borderRadius: 8, padding: '9px 14px', cursor: 'pointer',
                color: '#2ecc71', fontSize: 14, fontWeight: 600,
              }}
            >
              ➕ New Person
            </button>
          </div>
        )}

        {/* ── NEW STEP ── */}
        {step === 'new' && (
          <form onSubmit={handleNewSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {existingPersons.length > 0 && (
              <button
                type="button"
                onClick={() => { setStep('pick'); setErr(''); }}
                style={{
                  alignSelf: 'flex-start', background: 'transparent', border: 'none',
                  color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: 0,
                }}
              >
                ← Back
              </button>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Name</label>
              <input
                ref={inputRef}
                className="alert-inline-input"
                style={{ padding: '9px 12px', fontSize: 14 }}
                placeholder="Full name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Profile</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['day', 'night'].map(p => (
                  <button key={p} type="button"
                    className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                    onClick={() => setProfile(p)}>
                    {p === 'day' ? '☀️ Day' : '🌙 Night'}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Photo</label>
              <div className="pk-upload-zone" onClick={() => fileRef.current.click()}>
                {file ? `✅ ${file.name}` : '📷 Click to select photo…'}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => setFile(e.target.files[0] || null)} />
              </div>
            </div>
            {err && (
              <div style={{ fontSize: 12, color: '#e74c3c', padding: '6px 10px',
                background: 'rgba(231,76,60,0.08)', borderRadius: 6, border: '1px solid rgba(231,76,60,0.2)' }}>
                {err}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" className="pk-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="pk-btn primary" disabled={busy}>
                {busy ? '⏳ Uploading…' : '✓ Enroll'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function KnownFacesPage() {
  const PER_PAGE = 20;

  const [persons,      setPersons]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [hasMore,      setHasMore]      = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [fetching,     setFetching]     = useState(false);
  const [toast,        setToast]        = useState(null);
  const [expandedSet,  setExpandedSet]  = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);

  const sentinelRef  = useRef(null);
  const fetchingRef  = useRef(false);
  const pageRef      = useRef(0);
  const hasMoreRef   = useRef(false);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const loadPage = useCallback(async (p, reset = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoading(true);
    else       setFetching(true);
    try {
      const r = await apiFetch(`/ui/faces/known?page=${p}&per_page=${PER_PAGE}`);
      const d = await r.json();
      const items = d.persons || [];
      setPersons(prev => reset ? items : [...prev, ...items]);
      setTotal(d.total || 0);
      pageRef.current = p;
      const more = items.length === PER_PAGE;
      hasMoreRef.current = more;
      setHasMore(more);
    } catch {
      showToast('Failed to load known faces', 'err');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setFetching(false);
    }
  }, []);

  useEffect(() => { loadPage(0, true); }, [loadPage]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || fetching || loading) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !fetchingRef.current) {
        loadPage(pageRef.current + 1, false);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, fetching, loading, loadPage]);

  const refresh = () => {
    pageRef.current    = 0;
    hasMoreRef.current = false;
    loadPage(0, true);
  };

  const expandAll   = () => setExpandedSet(new Set(persons.map(p => p.name)));
  const collapseAll = () => setExpandedSet(new Set());
  const toggleOne   = (name) => setExpandedSet(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  return (
    <div className="pk-page">
      <div className="page-toolbar">
        <span className="tb-label">{total} enrolled</span>
        <div className="tb-sep" />
        <button className="tb-ghost-btn" onClick={expandAll}>Expand All</button>
        <button className="tb-ghost-btn" onClick={collapseAll}>Collapse All</button>
        <div className="tb-sep" />
        <button className="tb-ghost-btn" onClick={refresh}>↺ Refresh</button>
        <div className="tb-sep" />
        <button className="pk-btn primary" onClick={() => setShowAddModal(true)}>
          ➕ Add Person
        </button>
      </div>

      {loading ? (
        <div className="pk-loading">⏳ Loading…</div>
      ) : persons.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-empty-icon">🙅</div>
          <h3>No known faces</h3>
          <p>Add people so the system can recognise them.</p>
        </div>
      ) : (
        <div className="kf-list">
          {persons.map(p => (
            <PersonCard
              key={p.name}
              person={p}
              showToast={showToast}
              expanded={expandedSet.has(p.name)}
              onToggle={() => toggleOne(p.name)}
              onRemoved={name => { setPersons(prev => prev.filter(x => x.name !== name)); setTotal(t => t - 1); }}
              onBanned={name  => { setPersons(prev => prev.filter(x => x.name !== name)); setTotal(t => t - 1); }}
              onRenamed={(oldName, newName) => setPersons(prev => prev.map(x => x.name === oldName ? { ...x, name: newName } : x))}
            />
          ))}
        </div>
      )}

      {!loading && <div ref={sentinelRef} style={{ height: 1 }} />}
      {fetching && <div className="pk-loading" style={{ padding: '10px 0' }}>Loading more…</div>}

      {showAddModal && (
        <AddPersonModal
          existingPersons={persons.map(p => p.name)}
          onClose={() => setShowAddModal(false)}
          onAdded={(name) => {
            setShowAddModal(false);
            showToast(`Photo added for ${name}`);
            refresh();
          }}
        />
      )}

      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}
